import { Camera, Check, ImagePlus, LoaderCircle, Trash2, X } from 'lucide-react'
import { type ChangeEvent, type FormEvent, useEffect, useRef, useState } from 'react'

const MAX_BIO_LENGTH = 160
const MAX_AVATAR_BYTES = 5 * 1024 * 1024
const ACCEPTED_AVATAR_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp'])

export type EditableProfileMember = {
  id: string
  name: string
  handle: string
  initials: string
  avatarUrl?: string | null
  bio?: string | null
}

type AvatarAction = 'unchanged' | 'upload' | 'remove'

type EditProfileDialogProps = {
  open: boolean
  member: EditableProfileMember
  onClose: () => void
  onMemberChange: (member: Record<string, unknown>) => void
  onSaved: () => void
}

class ProfileRequestError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ProfileRequestError'
  }
}

async function memberResponse(response: Response): Promise<Record<string, unknown>> {
  const payload = await response.json().catch(() => ({})) as { member?: Record<string, unknown>; message?: string }
  if (!response.ok) throw new ProfileRequestError(payload.message || 'Your profile could not be updated.')
  if (!payload.member) throw new ProfileRequestError('Your updated profile could not be loaded.')
  return payload.member
}

export default function EditProfileDialog({ open, member, onClose, onMemberChange, onSaved }: EditProfileDialogProps) {
  const [bio, setBio] = useState(member.bio ?? '')
  const [savedBio, setSavedBio] = useState(member.bio ?? '')
  const [initialAvatarUrl, setInitialAvatarUrl] = useState<string | null>(member.avatarUrl ?? null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(member.avatarUrl ?? null)
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [avatarAction, setAvatarAction] = useState<AvatarAction>('unchanged')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const dialogRef = useRef<HTMLElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const objectUrlRef = useRef<string | null>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)
  const wasOpen = useRef(false)
  const busyRef = useRef(false)
  const closeRef = useRef(onClose)

  function releaseObjectUrl() {
    if (!objectUrlRef.current) return
    URL.revokeObjectURL(objectUrlRef.current)
    objectUrlRef.current = null
  }

  useEffect(() => {
    busyRef.current = busy
  }, [busy])

  useEffect(() => {
    closeRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (open && !wasOpen.current) {
      const nextBio = member.bio ?? ''
      const nextAvatar = member.avatarUrl ?? null
      setBio(nextBio)
      setSavedBio(nextBio)
      setInitialAvatarUrl(nextAvatar)
      setPreviewUrl(nextAvatar)
      setAvatarFile(null)
      setAvatarAction('unchanged')
      setBusy(false)
      setError('')
      setNotice('')
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
    if (!open && wasOpen.current) releaseObjectUrl()
    wasOpen.current = open
  }, [member.avatarUrl, member.bio, member.id, open])

  useEffect(() => () => releaseObjectUrl(), [])

  useEffect(() => {
    if (!open) return
    previouslyFocused.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const focusTimer = window.setTimeout(() => dialogRef.current?.focus(), 20)

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busyRef.current) {
        closeRef.current()
        return
      }
      if (event.key !== 'Tab') return
      const focusable = [...(dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), [href], [tabindex]:not([tabindex="-1"])',
      ) ?? [])].filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null)
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && (document.activeElement === dialogRef.current || document.activeElement === first)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.clearTimeout(focusTimer)
      window.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
      previouslyFocused.current?.focus()
    }
  }, [open])

  if (!open) return null

  const bioLength = [...bio].length
  const dirty = bio !== savedBio || avatarAction !== 'unchanged'

  function chooseAvatar(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return
    setError('')
    setNotice('')
    if (!ACCEPTED_AVATAR_TYPES.has(file.type)) {
      event.target.value = ''
      setError('Choose a JPG, PNG, or WebP image.')
      return
    }
    if (file.size > MAX_AVATAR_BYTES) {
      event.target.value = ''
      setError('Choose an image smaller than 5 MB.')
      return
    }

    releaseObjectUrl()
    const localUrl = URL.createObjectURL(file)
    objectUrlRef.current = localUrl
    setAvatarFile(file)
    setPreviewUrl(localUrl)
    setAvatarAction('upload')
    setNotice('New photo selected. Save when it looks right.')
  }

  function removeAvatar() {
    releaseObjectUrl()
    setAvatarFile(null)
    setPreviewUrl(null)
    setError('')
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (initialAvatarUrl) {
      setAvatarAction('remove')
      setNotice('Your photo will be removed when you save.')
    } else {
      setAvatarAction('unchanged')
      setNotice('Photo selection cleared.')
    }
  }

  async function saveProfile(event: FormEvent) {
    event.preventDefault()
    if (busy || !dirty || bioLength > MAX_BIO_LENGTH) return
    setBusy(true)
    setError('')
    setNotice('')
    const cleanBio = bio.trim()

    try {
      if (cleanBio !== savedBio) {
        const updatedMember = await memberResponse(await fetch('/api/account/profile', {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ bio: cleanBio }),
        }))
        setSavedBio(cleanBio)
        setBio(cleanBio)
        onMemberChange(updatedMember)
      }

      if (avatarAction === 'upload' && avatarFile) {
        const updatedMember = await memberResponse(await fetch('/api/account/avatar', {
          method: 'PUT',
          headers: { 'content-type': avatarFile.type },
          body: avatarFile,
        }))
        onMemberChange(updatedMember)
      } else if (avatarAction === 'remove') {
        const updatedMember = await memberResponse(await fetch('/api/account/avatar', { method: 'DELETE' }))
        onMemberChange(updatedMember)
      }

      releaseObjectUrl()
      onSaved()
      onClose()
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Your profile could not be updated.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="profile-editor-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.currentTarget === event.target && !busy) onClose()
    }}>
      <section
        ref={dialogRef}
        className="profile-editor"
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-editor-title"
        aria-describedby="profile-editor-description"
        tabIndex={-1}
      >
        <button className="profile-editor__close" type="button" aria-label="Close edit profile" onClick={onClose} disabled={busy}><X size={20} /></button>

        <header className="profile-editor__heading">
          <span>YOUR JOURNEY90 PROFILE</span>
          <h2 id="profile-editor-title">Make it feel like you</h2>
          <p id="profile-editor-description">Add a friendly photo and a quick introduction for the fans you meet here.</p>
        </header>

        <form className="profile-editor__form" onSubmit={saveProfile}>
          <div className="profile-editor__photo-section">
            <div className={`profile-editor__preview${previewUrl ? ' has-photo' : ''}`}>
              {previewUrl ? <img src={previewUrl} alt={`Profile preview for ${member.name}`} /> : <span aria-hidden="true">{member.initials}</span>}
              <i aria-hidden="true"><Camera size={18} /></i>
            </div>
            <div className="profile-editor__photo-copy">
              <strong>Profile photo</strong>
              <span>JPG, PNG, or WebP. Up to 5 MB.</span>
              <div>
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={chooseAvatar} hidden />
                <button type="button" className="profile-editor__photo-button" onClick={() => fileInputRef.current?.click()} disabled={busy}>
                  <ImagePlus size={16} /> {previewUrl ? 'Change photo' : 'Add photo'}
                </button>
                {(previewUrl || avatarAction === 'upload') && (
                  <button type="button" className="profile-editor__remove-button" onClick={removeAvatar} disabled={busy}>
                    <Trash2 size={15} /> Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="profile-editor__identity" aria-label="Account identity">
            <span><small>Display name</small><strong>{member.name}</strong></span>
            <span><small>Handle</small><strong>{member.handle}</strong></span>
          </div>

          <label className="profile-editor__bio">
            <span><strong>Short bio</strong><small className={bioLength >= 145 ? 'is-near-limit' : ''}>{bioLength}/{MAX_BIO_LENGTH}</small></span>
            <textarea
              value={bio}
              maxLength={MAX_BIO_LENGTH}
              rows={4}
              placeholder="Tell fellow fans what you watch, rewatch, and absolutely need to discuss…"
              onChange={(event) => { setBio(event.target.value); setError(''); setNotice('') }}
              disabled={busy}
            />
          </label>

          <div className="profile-editor__feedback" aria-live="polite">
            {error ? <span className="is-error" role="alert">{error}</span> : notice ? <span><Check size={14} /> {notice}</span> : null}
          </div>

          <footer className="profile-editor__actions">
            <button type="button" className="profile-editor__cancel" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className="profile-editor__save" disabled={busy || !dirty || bioLength > MAX_BIO_LENGTH}>
              {busy ? <><LoaderCircle className="auth-spinner" size={17} /> Saving…</> : <><Check size={17} /> Save changes</>}
            </button>
          </footer>
        </form>
      </section>
    </div>
  )
}
